/**
 * Cookie-session refresh loop (ADR-0036).
 *
 * This is the piece that makes a brand-new tab work. The access token lives in
 * memory, so a fresh page load starts with nothing; the `penn_rt` HttpOnly
 * cookie spans tabs, so calling `/v1/auth/refresh` once at boot restores the
 * session before any route guard runs.
 *
 * Four triggers, all funnelled through ONE single-flight promise (the same
 * pattern `AxiosService`'s `pendingReExchange` already uses) so a tab regaining
 * focus while its timer fires while a 401 lands does not produce three
 * concurrent rotations — which, since every refresh rotates, would look like
 * token reuse and revoke the user's whole session family:
 *
 *   1. **boot** — before route guards render (AC 1: new tab stays signed in);
 *   2. **timer** — at ~80% of access-token life;
 *   3. **visibilitychange** — a backgrounded tab returning to focus with a
 *      stale token. This is the specific fix for the cascade: that tab used to
 *      hit a 401, fail to recover, and `broadcastLogout()` every other tab;
 *   4. **on 401** — driven from `AxiosService`, which calls `refreshSession()`.
 *
 * Failure is not a logout. `401 no-session` means "no cookie session here" —
 * an external user, a disabled mode, an expired session — and the caller falls
 * back to the existing Cognito re-exchange, i.e. exactly today's behaviour.
 */
import axios from "axios";

import type { AuthUser } from "../types/auth-types/AuthUser";
import { AUTH_COOKIE_SESSION } from "../config/auth-features";
import { webCommonHost } from "../config/host";
import { API_BASE_URL, setCsrfToken, readCsrfCookie } from "./api-session";

export interface RefreshOutcome {
  accessToken: string;
  user: AuthUser;
  expiresInSeconds: number;
}

/** Refresh at 80% of token life, floored so a short TTL cannot spin. */
const REFRESH_RATIO = 0.8;
const MIN_TIMER_MS = 60_000;
/** Treat a token with under 2 minutes left as stale when a tab regains focus. */
const STALE_THRESHOLD_MS = 120_000;

let inFlight: Promise<RefreshOutcome | null> | null = null;
let timerId: ReturnType<typeof setTimeout> | null = null;
let expiresAtMs = 0;
let onRefreshed: ((outcome: RefreshOutcome) => void) | null = null;
let listenersAttached = false;

/**
 * Called once from the app shell with the callback that pushes a new token and
 * user into Redux. Keeping the store out of this module leaves it importable
 * from `AxiosService` without a circular import.
 */
export function configureSessionRefresh(
  handler: (outcome: RefreshOutcome) => void,
): void {
  onRefreshed = handler;
}

/**
 * Single-flight refresh. Every trigger goes through here; concurrent callers
 * share one HTTP request and one rotation.
 */
export function refreshSession(): Promise<RefreshOutcome | null> {
  if (!AUTH_COOKIE_SESSION) return Promise.resolve(null);
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      // A direct axios call, NOT the app instance: the app's interceptors would
      // attach the expiring token and, worse, could recurse into this very
      // function on a 401.
      const csrf = readCsrfCookie();
      // Which SPA is asking: the cookie is Domain-scoped to the environment, so
      // svc-02 needs this to keep one app's session out of the other (issue 56).
      const response = await axios.post(
        `${API_BASE_URL}/v1/auth/refresh`,
        { appContext: webCommonHost().appContext },
        {
          timeout: 15_000,
          withCredentials: true,
          headers: csrf ? { "X-CSRF-Token": csrf } : {},
        },
      );
      const data = response.data?.data ?? response.data;
      const accessToken = data?.accessToken as string | undefined;
      const user = data?.user as AuthUser | undefined;
      if (!accessToken || !user) return null;

      const expiresInSeconds = Number(data?.expiresInSeconds) || 900;
      if (typeof data?.csrfToken === "string") setCsrfToken(data.csrfToken);

      const outcome: RefreshOutcome = { accessToken, user, expiresInSeconds };
      expiresAtMs = Date.now() + expiresInSeconds * 1000;
      onRefreshed?.(outcome);
      scheduleNextRefresh(expiresInSeconds);
      return outcome;
    } catch {
      // Includes `401 no-session`. Deliberately silent: the caller decides
      // whether to fall back or to show the login page, and a console error on
      // every anonymous page load would be noise.
      return null;
    } finally {
      // Cleared on the next tick so concurrent waiters all observe the same
      // result before a new flight can start.
      setTimeout(() => {
        inFlight = null;
      }, 0);
    }
  })();

  return inFlight;
}

/**
 * Boot restore. Awaited by the app shell before routes render, which is what
 * stops `ProtectedRoute` redirecting on the first paint.
 */
export async function restoreSession(): Promise<RefreshOutcome | null> {
  if (!AUTH_COOKIE_SESSION) return null;
  const outcome = await refreshSession();
  attachListeners();
  return outcome;
}

function scheduleNextRefresh(expiresInSeconds: number): void {
  if (timerId !== null) clearTimeout(timerId);
  const delay = Math.max(expiresInSeconds * 1000 * REFRESH_RATIO, MIN_TIMER_MS);
  timerId = setTimeout(() => {
    void refreshSession();
  }, delay);
}

/**
 * A backgrounded tab's timer can be throttled or coalesced to nothing by the
 * browser, so a tab that has been hidden for an hour may come back holding a
 * long-dead token. Refresh on the way back in rather than discovering it via a
 * 401 — that 401 is what used to log every tab out.
 */
function onVisibilityChange(): void {
  if (typeof document === "undefined" || document.visibilityState !== "visible") {
    return;
  }
  if (expiresAtMs === 0) return;
  if (expiresAtMs - Date.now() > STALE_THRESHOLD_MS) return;
  void refreshSession();
}

function attachListeners(): void {
  if (listenersAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  listenersAttached = true;
}

/** Stop the loop — called on logout so a dead session is not re-refreshed. */
export function stopSessionRefresh(): void {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
  expiresAtMs = 0;
  if (listenersAttached && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    listenersAttached = false;
  }
}

/** Test seam: reset module state between cases. */
export function __resetSessionRefreshForTests(): void {
  stopSessionRefresh();
  inFlight = null;
  onRefreshed = null;
}
